
import { Dayjs } from 'dayjs'
import { useState } from 'react'

import { closeRunsheet, MoveRunsheetAction } from '@/entities/schedules/thunks/closeRunsheet'
import { Button, Modal } from '@/shared/components'
import { ButtonDatePicker } from '@/shared/components/ButtonDatePicker/ButtonDatePicker'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { useDialogContext } from '@/shared/components/Modal/DialogContext'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { useAppDispatch } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { Grid, styled, Typography } from '@mui/material'
import { GridRowSelectionModel } from '@mui/x-data-grid-premium'

import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog'
import { useCloseRunsheetTableData } from './libs/useCloseRunsheetTableData'
import { useColumnGroupingModel } from './libs/useColumnGroupingModel'
import { useOpenOrdersColumnDefs } from './libs/useOpenOrdersColumnDefs'
import { useRunsheetScheduledDates } from './libs/useRunsheetScheduledDates'

export type CloseRunsheetDialogProps = DialogComponentProps & {
  onClose: () => void
}

export const CloseRunsheetDialog: React.FC<CloseRunsheetDialogProps> = ({
  open,
  onClose,
  runsheetId,
}) => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { openDialog } = useDialogContext()
  const { columnGroupingModel, initialColumnGroupVisibility } = useColumnGroupingModel()
  const { runsheetName, runsheetDateOfOperation, loading, rows, setRows } = useCloseRunsheetTableData(runsheetId)
  const { scheduledDates } = useRunsheetScheduledDates()

  const columns = useOpenOrdersColumnDefs()
  const dispatch = useAppDispatch()

  const onLeaveAsIs = () => {
    setRows(prevRows => {
      return prevRows.map(row => {
        if (rowSelectionModel.includes(row.id)) {
          return {
            ...row,
            action: '',
            newLocation: '** No Change **',
            newLocationCellStyle: {
              color: 'inherit',
              backgroundColor: 'inherit',
            },
          }
        }

        return row
      })
    })
  }

  const onMoveToInbox = () => {
    setRows(prevRows => {
      return prevRows.map(row => {
        if (rowSelectionModel.includes(row.id)) {
          return {
            ...row,
            action: 'inbox',
            newLocation: 'Inbox',
            newLocationCellStyle: {
              color: 'black',
              backgroundColor: 'gold',
            },
          }
        }

        return row
      })
    })
  }

  const onCompleteOrder = () => {
    setRows(prevRows => {
      return prevRows.map(row => {
        if (rowSelectionModel.includes(row.id)) {
          return {
            ...row,
            action: 'complete',
            newLocation: 'Completed by Supervisor',
            newLocationCellStyle: {
              color: 'black',
              backgroundColor: 'limegreen',
            },
          }
        }

        return row
      })
    })
  }

  const onCancelOrder = () => {
    setRows(prevRows => {
      return prevRows.map(row => {
        if (rowSelectionModel.includes(row.id)) {
          return {
            ...row,
            action: 'cancel',
            newLocation: 'Cancelled',
            newLocationCellStyle: {
              color: 'white',
              backgroundColor: 'black',
            },
          }
        }

        return row
      })
    })
  }

  const onMoveToRunsheet = (date: Dayjs | null) => {
    if (!date) return

    setRows(prevRows => {
      return prevRows.map(row => {
        if (rowSelectionModel.includes(row.id)) {
          return {
            ...row,
            action: 'move',
            newLocation: `Runsheet ${date.format('YYYY-MM-DD')}`,
            newLocationCellStyle: {
              color: 'white',
              backgroundColor: 'blue',
            },
            moveDateOfOperation: date.format('YYYY-MM-DD'),
          }
        }

        return row
      })
    })
  }

  const onCloseRunsheet = async () => {
    const onConfirm = () => {
      setIsSubmitting(true)

      const actions: MoveRunsheetAction[] = []

      for (let i = 0; i < rows.length; i++) {
        const openOrder = rows[i]

        if (openOrder.action) {
          actions.push({
            orderId: openOrder.id,
            orderStatusType: openOrder.statusType,
            action: openOrder.action,
            dateOfOperation: openOrder.moveDateOfOperation,
          })
        }
      }

      dispatch(closeRunsheet({
        runsheetId,
        runsheetDate: runsheetDateOfOperation,
        actions,
      })).then(() => {
        setIsSubmitting(false)
        onClose()
      }).finally(() => {
        setIsSubmitting(false)
      })
    }

    openDialog(ConfirmDialog, {
      title: 'Confirm Close Runsheet',
      message: 'Changes will be applied against the selected Orders associated to this Runsheet. Do you wish to continue?',
      onConfirm: onConfirm,
      confirmText: 'Confirm',
    })
  }

  return (
    <Modal
      title={`Close Runsheet for ${runsheetName}:`}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
      disableClose={isSubmitting}
    >
      <StyledContent>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>Select the open orders and move them if necessary:</Typography>
        <StyledToolbar container direction={'row'} alignItems={'center'} gap={1}>
          <Grid item>
            <Button secondary disabled={rowSelectionModel.length === 0} onClick={onLeaveAsIs}>Leave as is</Button>
          </Grid>
          <Grid item>
            <Button secondary disabled={rowSelectionModel.length === 0} onClick={onMoveToInbox}>Move to inbox</Button>
          </Grid>
          <Grid item>
            <Button secondary disabled={rowSelectionModel.length === 0} onClick={onCompleteOrder}>Complete order</Button>
          </Grid>
          <Grid item>
            <Button secondary disabled={rowSelectionModel.length === 0} onClick={onCancelOrder}>Cancel order</Button>
          </Grid>
          <Grid item>
            <ButtonDatePicker
              onChange={onMoveToRunsheet}
              disabled={rowSelectionModel.length === 0}
              highlightedDates={scheduledDates}
              slotProps={{
                button: {
                  secondary: true,
                },
                calendar: {
                  disablePast: true,
                },
              }}
            >
              Move to runsheet
            </ButtonDatePicker>
          </Grid>
        </StyledToolbar>
        <GridTable
          checkboxSelection
          disableVirtualization
          rows={rows}
          columns={columns}
          loading={loading}
          columnGroupingModel={columnGroupingModel}
          initialColumnGroupVisibility={initialColumnGroupVisibility}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel)
          }}
          sx={{
            height: 340,
          }}
        />
      </StyledContent>
      <StyledFormActions>
        {onClose && <Button text onClick={onClose}>Cancel</Button>}
        <Button primary onClick={onCloseRunsheet}>
          Close Runsheet
        </Button>
      </StyledFormActions>
    </Modal>
  )
}

const StyledToolbar = styled(Grid)`
  padding: ${pxToRem(8)} 0 ${pxToRem(4)} ${pxToRem(24)};
  width: calc(100% + ${pxToRem(32)});
  margin: 0 ${pxToRem(-24)} ${pxToRem(12)} ${pxToRem(-24)};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`

const StyledContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    min-width: ${pxToRem(540)};
    max-width: 80dvw;
  }
`

const StyledFormActions = styled('div')`
  width: 100%;
  padding-bottom: ${pxToRem(12)};
  display: flex;
  justify-content: flex-end;
  gap: ${pxToRem(12)};
  padding-top: ${pxToRem(8)};
  background-color: ${(props) => props.theme.palette.background.paper};
`